import React, { useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';

const RadarChart = ({ data, compareData, radarColor, divWidth, maxValue }) => {
  const svgRef = useRef(null);

  const toNumber = useCallback((v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    if (v == null) return 0;

    const s = String(v).trim().replace('%', '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const normalizeToSeriesArray = useCallback(
    (input) => {
      if (!Array.isArray(input) || input.length === 0) return [];
      const seriesArr = Array.isArray(input[0]) ? input : [input];

      return seriesArr
        .filter((serie) => Array.isArray(serie) && serie.length > 0)
        .map((serie) =>
          serie.map((p) => ({
            axis: p?.axis ?? '',
            value: toNumber(p?.value),
          })),
        );
    },
    [toNumber],
  );

  const alignCompareToMain = (mainSerie, compareSerie) => {
    const order = mainSerie.map((p) => p.axis);
    const map = new Map(compareSerie.map((p) => [p.axis, p.value]));
    return order.map((axis) => ({ axis, value: map.has(axis) ? map.get(axis) : 0 }));
  };

  const drawChart = useCallback(
    (svg) => {
      const MIN_SIZE = 150;
      const maxValuex = maxValue ?? 100;

      const mainSeriesArr = normalizeToSeriesArray(data);
      const compareSeriesArr = normalizeToSeriesArray(compareData);

      if (!divWidth || mainSeriesArr.length === 0 || !mainSeriesArr[0]?.length) {
        svg.selectAll('*').remove();
        return;
      }

      const mainSerie = mainSeriesArr[0];
      let seriesToDraw = [mainSerie];

      if (compareSeriesArr.length > 0 && compareSeriesArr[0]?.length) {
        const alignedCompare = alignCompareToMain(mainSerie, compareSeriesArr[0]);
        seriesToDraw = [mainSerie, alignedCompare];
      }

      const margin = { top: 100, right: 100, bottom: 100, left: 100 };

      let rawWidth = Math.min(divWidth, window.innerWidth - 10) - margin.left - margin.right;
      let width = Math.max(rawWidth, MIN_SIZE);

      let height = Math.max(
        Math.min(width, window.innerHeight - margin.top - margin.bottom - 20),
        MIN_SIZE,
      );

      if (window.screen.width >= 3000) {
        width = Math.max(width - 200, MIN_SIZE);
        height = Math.max(height + 100, MIN_SIZE);
      } else if (window.screen.width >= 1920) {
        width = Math.max(width - 50, MIN_SIZE);
        height = Math.max(height + 25, MIN_SIZE);
      }

      const wrapWidth = Math.max((divWidth * 24) / 100, 80);
      const labelFactor = divWidth / ((divWidth * 70) / 100) || 1.25;

      const options = {
        w: width,
        h: height,
        margin,
        maxValue: maxValuex,
        levels: 5,
        roundStrokes: false,
        wrapWidth,
        labelFactor,
      };

      renderRadar(svg, seriesToDraw, options, radarColor, divWidth, maxValuex);

      function renderRadar(
        svgSel,
        chartSeries,
        cfgInput,
        radarFillColor,
        containerWidth,
        maxValFallback,
      ) {
        const cfg = {
          w: 600,
          h: 600,
          margin: { top: 20, right: 20, bottom: 20, left: 20 },
          levels: 10,
          maxValue: maxValFallback,
          labelFactor: 1.25,
          wrapWidth: 130,
          opacityArea: 0.12,
          dotRadius: 5,
          opacityCircles: 0.1,
          strokeWidth: 2,
          roundStrokes: false,
          ...cfgInput,
        };

        const computedMax = Math.max(
          cfg.maxValue,
          d3.max(chartSeries, (serie) => d3.max(serie, (o) => o.value)),
        );

        const allAxis = chartSeries[0].map((d) => d.axis);
        const total = allAxis.length;
        const radius = Math.min(cfg.w / 2, cfg.h / 2);
        const angleSlice = (Math.PI * 2) / total;

        const rScale = d3.scaleLinear().range([0, radius]).domain([0, computedMax]);

        svgSel.selectAll('*').remove();

        svgSel
          .attr('width', cfg.w + cfg.margin.left + cfg.margin.right)
          .attr('height', cfg.h + cfg.margin.top + cfg.margin.bottom)
          .attr('class', 'radarChart')
          .style('overflow', 'visible');

        const g = svgSel
          .append('g')
          .attr(
            'transform',
            `translate(${cfg.w / 2 + cfg.margin.left},${cfg.h / 2 + cfg.margin.top})`,
          );

        // glow
        const filter = g.append('defs').append('filter').attr('id', 'glow');
        filter.append('feGaussianBlur').attr('stdDeviation', '2.5').attr('result', 'coloredBlur');
        const feMerge = filter.append('feMerge');
        feMerge.append('feMergeNode').attr('in', 'coloredBlur');
        feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

        // grid
        const axisGrid = g.append('g').attr('class', 'axisWrapper');

        axisGrid
          .selectAll('.levels')
          .data(d3.range(1, cfg.levels + 1).reverse())
          .enter()
          .append('circle')
          .attr('class', 'gridCircle')
          .attr('r', (d) => (radius / cfg.levels) * d)
          .style('fill', '#ebebeb')
          .style('stroke', '#ebebeb')
          .style('fill-opacity', cfg.opacityCircles)
          .style('filter', 'url(#glow)')
          .style('pointer-events', 'none');

        axisGrid
          .selectAll('.axisLabel')
          .data(d3.range(1, cfg.levels + 1).reverse())
          .enter()
          .append('text')
          .attr('class', 'axisLabel')
          .attr('x', 4)
          .attr('y', (d) => (-d * radius) / cfg.levels)
          .attr('dy', '0.4em')
          .style('font-size', '0.7rem')
          .attr('fill', '#C2C923')
          .text((d) => d * (cfg.maxValue / cfg.levels))
          .style('pointer-events', 'none');

        const axis = axisGrid
          .selectAll('.axis')
          .data(allAxis)
          .enter()
          .append('g')
          .attr('class', 'axis');

        axis
          .append('line')
          .attr('x1', 0)
          .attr('y1', 0)
          .attr('x2', (d, i) => rScale(computedMax * 1.1) * Math.cos(angleSlice * i - Math.PI / 2))
          .attr('y2', (d, i) => rScale(computedMax * 1.1) * Math.sin(angleSlice * i - Math.PI / 2))
          .attr('class', 'line')
          .style('stroke', 'white')
          .style('stroke-width', '2px')
          .style('pointer-events', 'none');

        const labelFontPx = Math.max(10, (containerWidth * 3.4223) / 100);

        axis
          .append('text')
          .attr('class', 'legend')
          .style('font-size', `${labelFontPx}px`)
          .attr('text-anchor', 'middle')
          .attr('dy', '0em')
          .attr(
            'x',
            (d, i) =>
              rScale(computedMax * cfg.labelFactor) * Math.cos(angleSlice * i - Math.PI / 2),
          )
          .attr(
            'y',
            (d, i) =>
              rScale(computedMax * cfg.labelFactor) * Math.sin(angleSlice * i - Math.PI / 2),
          )
          .text((d) => d)
          .call(wrap, cfg.wrapWidth)
          .style('pointer-events', 'none');

        let radarLine = d3
          .lineRadial()
          .curve(d3.curveLinearClosed)
          .radius((d) => rScale(d.value))
          .angle((d, i) => i * angleSlice);

        if (cfg.roundStrokes) radarLine = radarLine.curve(d3.curveCardinalClosed);

        // ✅ LAYERS: prima disegni tutto, poi hover, poi tooltip in cima
        const blobWrapper = g
          .selectAll('.radarWrapper')
          .data(chartSeries)
          .enter()
          .append('g')
          .attr('class', 'radarWrapper');

        const has2 = chartSeries.length > 1;
        const areaOpacityByIndex = (i) => (i === 0 ? cfg.opacityArea : 0.18);
        const strokeOpacityByIndex = (i) => (i === 0 ? 0.6 : 0.55);

        const base = radarFillColor || '#739CC4';
        const mainCol = d3.color(base);
        const compareCol = mainCol ? mainCol.darker(1) : d3.color('#4d6f8f');
        const colorByIndex = (i) => (i === 0 ? mainCol : compareCol);

        blobWrapper
          .append('path')
          .attr('class', 'radarArea')
          .attr('d', (d) => radarLine(d))
          .style('fill', (d, i) => colorByIndex(i))
          .style('fill-opacity', (d, i) => (has2 ? areaOpacityByIndex(i) : cfg.opacityArea))
          .style('pointer-events', 'none');

        blobWrapper
          .append('path')
          .attr('class', 'radarStroke')
          .attr('d', (d) => radarLine(d))
          .style('stroke-width', `${cfg.strokeWidth}px`)
          .style('stroke', (d, i) => colorByIndex(i))
          .style('fill', 'none')
          .style('opacity', (d, i) => (has2 ? strokeOpacityByIndex(i) : 0.7))
          .style('filter', 'url(#glow)')
          .style('pointer-events', 'none');

        blobWrapper
          .selectAll('.radarCircle')
          .data((serie, serieIndex) => serie.map((p) => ({ ...p, __serieIndex: serieIndex })))
          .enter()
          .append('circle')
          .attr('class', 'radarCircle')
          .attr('r', cfg.dotRadius)
          .attr('cx', (d, i) => rScale(d.value) * Math.cos(angleSlice * i - Math.PI / 2))
          .attr('cy', (d, i) => rScale(d.value) * Math.sin(angleSlice * i - Math.PI / 2))
          .style('fill', (d) => colorByIndex(d.__serieIndex))
          .style('fill-opacity', 0.9)
          .style('pointer-events', 'none');

        // ✅ hoverLayer sopra ai radar
        const hoverLayer = g.append('g').attr('class', 'hoverLayer');

        const hoverPoints = chartSeries.flatMap((serie, serieIndex) =>
          serie.map((p, axisIndex) => ({
            axis: p.axis,
            value: p.value,
            axisIndex,
            __serieIndex: serieIndex,
          })),
        );

        // ✅ tooltipLayer ANCORA SOPRA (ultimo) => sempre in primo piano
        const tooltipLayer = g.append('g').attr('class', 'tooltipLayer');

        const tooltipG = tooltipLayer
          .append('g')
          .attr('class', 'radarTooltip')
          .style('opacity', 0)
          .style('pointer-events', 'none');

        const tooltipRect = tooltipG
          .append('rect')
          .attr('rx', 8)
          .attr('ry', 8)
          .style('fill', 'rgba(0, 0, 0, 0.9)')
          .style('stroke', 'none');

        const tooltipText = tooltipG
          .append('text')
          .attr('class', 'radarTooltipText')
          .style('font-size', '14px')
          .style('fill', '#fff');

        const setTooltip = ({ x, y, lines }) => {
          tooltipText.selectAll('*').remove();
          lines.forEach((line, idx) => {
            tooltipText
              .append('tspan')
              .attr('x', x + 12)
              .attr('y', y + 20 + idx * 18)
              .text(line);
          });

          const node = tooltipText.node();
          const bbox = node ? node.getBBox() : { width: 0, height: 0 };

          tooltipRect
            .attr('x', x)
            .attr('y', y)
            .attr('width', bbox.width + 24)
            .attr('height', bbox.height + 20);
        };

        const showTooltip = (payload) => {
          tooltipG.interrupt().style('opacity', 1);
          setTooltip(payload);
        };

        const hideTooltip = () => {
          tooltipG.interrupt().transition().duration(150).style('opacity', 0);
        };

        hoverLayer
          .selectAll('.radarInvisibleCircle')
          .data(hoverPoints)
          .enter()
          .append('circle')
          .attr('class', 'radarInvisibleCircle')
          .attr('r', cfg.dotRadius * 2)
          .attr('cx', (d) => rScale(d.value) * Math.cos(angleSlice * d.axisIndex - Math.PI / 2))
          .attr('cy', (d) => rScale(d.value) * Math.sin(angleSlice * d.axisIndex - Math.PI / 2))
          .style('fill', 'transparent')
          .style('pointer-events', 'all')
          .on('mouseover', function (event, d) {
            const labelSerie = d.__serieIndex === 0 ? 'Progetto Originario' : 'Variante Monitorata';

            const cx = parseFloat(d3.select(this).attr('cx'));
            const cy = parseFloat(d3.select(this).attr('cy'));

            const x = cx + 12;
            const y = cy - 12 - 60;

            showTooltip({
              x,
              y,
              lines: [
                d.axis,
                labelSerie,
                `${d.value.toLocaleString('it-IT', { maximumFractionDigits: 2 })}%`,
              ],
            });
          })
          .on('mouseout', hideTooltip);

        function wrap(text, width) {
          text.each(function () {
            const textSel = d3.select(this);
            const words = textSel.text().split(/\s+/).reverse();
            let word;
            let line = [];
            let lineNumber = 0;
            const lineHeight = 1.4;
            const y = textSel.attr('y');
            const x = textSel.attr('x');
            const dy = parseFloat(textSel.attr('dy'));

            let tspan = textSel
              .text(null)
              .append('tspan')
              .attr('x', x)
              .attr('y', y)
              .attr('dy', `${dy}em`);

            while ((word = words.pop())) {
              line.push(word);
              tspan.text(line.join(' '));
              if (tspan.node().getComputedTextLength() > width) {
                line.pop();
                tspan.text(line.join(' '));
                line = [word];
                tspan = textSel
                  .append('tspan')
                  .attr('x', x)
                  .attr('y', y)
                  .attr('dy', `${++lineNumber * lineHeight + dy}em`)
                  .text(word);
              }
            }
          });
        }
      }
    },
    [data, compareData, radarColor, divWidth, maxValue, normalizeToSeriesArray],
  );

  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    drawChart(svg);
  }, [drawChart, divWidth]);

  return <svg ref={svgRef} />;
};

export default RadarChart;
